const BASE_URL: string = import.meta.env.VITE_BASE_URL || "";

// 新APIは /threads, /posts 直下。/api プレフィックスは廃止 (§10)
export const API_BASE: string = BASE_URL;

export type ApiErrorCode =
  | "VALIDATION_ERROR"
  | "AUTHENTICATION_FAILED"
  | "PERMISSION_DENIED"
  | "NOT_FOUND"
  | "CONFLICT"
  | "GONE"
  | "RATE_LIMIT_EXCEEDED"
  | "INTERNAL_SERVER_ERROR"
  | "OGP_FETCH_FAILED" // viewer 側で正規化する code (§6.5)
  | (string & {}); // 未知の code も string で受ける

export class ApiError extends Error {
  status?: number;
  code?: ApiErrorCode;
  details?: unknown;

  constructor(
    message: string,
    status?: number,
    code?: ApiErrorCode,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type ApiFailure = {
  success: false;
  error: string;
  code: string;
  details?: unknown;
};

function unwrap<T>(payload: unknown, status: number): T {
  // 204 / 空 body は undefined を返す。呼び出し側は apiDelete<void>() 等で受ける
  if (status === 204 || payload === null) return undefined as T;
  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    if (p.success === false) {
      const fail = p as ApiFailure;
      throw new ApiError(fail.error, status, fail.code, fail.details);
    }
    if (p.success === true && "data" in p) return p.data as T;
    if ("items" in p) return p as T;
    if ("data" in p) return p.data as T;
  }
  // OpenAPI 生成型と一致しない素の JSON はそのまま返す
  return payload as T;
}

async function request<T>(
  method: string,
  path: string,
  body?: FormData | object,
): Promise<T> {
  const isFormData = body instanceof FormData;
  const init: RequestInit = {
    method,
    headers:
      isFormData || body == null
        ? { Accept: "application/json" }
        : { Accept: "application/json", "Content-Type": "application/json" },
  };
  if (body != null) init.body = isFormData ? body : JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, init);
  // 204 等は body 無し。JSON parse 失敗は raw text を ApiError に詰めて throw
  const text = await res.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(
        `Invalid JSON response (HTTP ${res.status})`,
        res.status,
        undefined,
        text.slice(0, 200),
      );
    }
  }
  if (!res.ok) {
    if (payload && typeof payload === "object" && "success" in payload) {
      // 失敗形を unwrap で throw させる
      return unwrap<T>(payload, res.status);
    }
    throw new ApiError(`HTTP ${res.status}`, res.status);
  }
  return unwrap<T>(payload, res.status);
}

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

export async function apiPost<T>(
  path: string,
  body: FormData | object,
): Promise<T> {
  return request<T>("POST", path, body);
}

export async function apiPut<T>(
  path: string,
  body?: FormData | object,
): Promise<T> {
  return request<T>("PUT", path, body);
}

export async function apiPatch<T>(
  path: string,
  body?: FormData | object,
): Promise<T> {
  return request<T>("PATCH", path, body);
}

// 204 / 空 body エンドポイントは apiDelete<void>() で呼ぶ
export async function apiDelete<T = void>(
  path: string,
  body?: FormData | object,
): Promise<T> {
  return request<T>("DELETE", path, body);
}
